import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Typography, CircularProgress, Stack, Tooltip } from '@mui/material';
import { saveForecast } from '../../../../repositories/HttpRepository';
import { toast } from 'react-toastify';
import { ToastMessage } from '../../../../constants/toastMessage';
import { parseMapKey } from '../../utils/transformData';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { getRollingWeeksLabel } from '../../../../hooks/Forecast/useRollingWeeksLabel';
import { useRecoilValue } from 'recoil';
import { getStatisticForecast } from '../../../../atoms/Forecast';

const getRollingWeeksText = (numberOfWeeks) => (numberOfWeeks ? `${numberOfWeeks}-week` : 'weekly');

const getFieldConfig = (numberOfWeeks) => [
  {
    key: 'latest_routes',
    label: 'Latest Routes',
    readOnly: true,
  },
  {
    key: 'rolling_weeks',
    label: getRollingWeeksLabel(numberOfWeeks),
    readOnly: true,
  },
  {
    key: 'min_tour_cost',
    label: 'Min Tour Cost',
    placeholder: 'Enter min tour cost',
    step: 1,
    min: 0,
    validation: 'validTourCost',
    errorMsg: 'Min tour cost must be ≤ Max tour cost',
    infoTooltip: `This number is calculated on the ${getRollingWeeksText(numberOfWeeks)} rolling minimum of actual routes`,
  },
  {
    key: 'max_tour_cost',
    label: 'Max Tour Cost',
    placeholder: 'Enter max tour cost',
    step: 1,
    min: 0,
    validation: 'validTourCost',
    errorMsg: 'Max tour cost must be ≥ Min tour cost',
    infoTooltip: `This number is calculated on the ${getRollingWeeksText(numberOfWeeks)} rolling maximum of actual routes`,
  },
  {
    key: 'min_shipment_count',
    label: 'Min Shipment Count',
    placeholder: 'Enter min shipment',
    step: 1,
    validation: 'validShipmentCount',
    errorMsg: 'Min shipment count must be ≤ Max shipment count',
    min: 0,
  },
  {
    key: 'max_shipment_count',
    label: 'Max Shipment Count',
    placeholder: 'Enter max shipment',
    step: 1,
    min: 0,
    validation: 'validShipmentCount',
    errorMsg: 'Max shipment count must be ≥ Min shipment count',
  },
  {
    key: 'min_ocac',
    label: 'Min OCAC',
    placeholder: 'Enter min OCAC',
    step: 0.01,
    min: 0,
    validation: 'validOCAC',
    errorMsg: 'Min OCAC must be ≤ Max OCAC',
  },
  {
    key: 'max_ocac',
    label: 'Max OCAC',
    placeholder: 'Enter max OCAC',
    step: 0.01,
    min: 0,
    validation: 'validOCAC',
    errorMsg: 'Max OCAC must be ≥ Min OCAC',
  },
];

const EditInfoModal = ({ open, onClose, data, mapKey, onSaveSuccess }) => {
  const { forecastId, zoneId } = parseMapKey(mapKey);
  const [loading, setLoading] = useState(false);
  const statisticData = useRecoilValue(getStatisticForecast);
  const FIELD_CONFIG = getFieldConfig(statisticData?.numberOfWeeks);
  const [formData, setFormData] = useState({
    min_tour_cost: '',
    max_tour_cost: '',
    min_shipment_count: '',
    max_shipment_count: '',
    min_ocac: '',
    max_ocac: '',
  });

  const validTourCost = formData.min_tour_cost === '' || formData.max_tour_cost === '' || Number(formData.min_tour_cost) <= Number(formData.max_tour_cost);
  const validShipmentCount = formData.min_shipment_count === '' || formData.max_shipment_count === '' || Number(formData.min_shipment_count) <= Number(formData.max_shipment_count);
  const validOCAC = formData.min_ocac === '' || formData.max_ocac === '' || Number(formData.min_ocac) <= Number(formData.max_ocac);
  const isValid = validTourCost && validShipmentCount && validOCAC;

  const validations = {
    validTourCost,
    validShipmentCount,
    validOCAC,
  };

  // Update form data when modal opens or data changes
  useEffect(() => {
    if (open && data) {
      setFormData({
        min_tour_cost: data?.min_tour_cost ?? '',
        max_tour_cost: data?.max_tour_cost ?? '',
        min_shipment_count: data?.min_shipment_count ?? '',
        max_shipment_count: data?.max_shipment_count ?? '',
        min_ocac: data?.min_ocac ?? '',
        max_ocac: data?.max_ocac ?? '',
      });
    }
  }, [open, data]);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleBlur = (field) => (event) => {
    if (event?.target?.value === '') {
      setFormData((prev) => ({
        ...prev,
        [field]: 0,
      }));
    }
  };

  const handleSave = async () => {
    if (!forecastId || !zoneId || !isValid) return;

    setLoading(true);
    try {
      const payload = [
        {
          id: forecastId,
          groups: [
            {
              id: zoneId,
              ...formData,
            },
          ],
        },
      ];

      const response = await saveForecast(payload);

      if (response.ok) {
        toast.success(ToastMessage.UPDATED_SUCCESS);
        // Notify parent to update local state
        if (onSaveSuccess) {
          onSaveSuccess(mapKey, formData);
        }
        onClose();
      } else {
        // console.error('Failed to save info:', response);
        toast.error(ToastMessage.UPDATED_FAIL);
      }
    } catch (error) {
      // console.error('Error saving info:', error);
      toast.error(ToastMessage.UPDATED_FAIL);
    } finally {
      setLoading(false);
    }
  };

  const renderField = (field) => {
    const { key, label, readOnly, placeholder, step, validation, errorMsg, min, infoTooltip } = field;
    const fieldValid = validation ? validations[validation] : true;

    return (
      <Box key={key}>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {label}
          </Typography>
          {infoTooltip && (
            <Tooltip title={infoTooltip}>
              <InfoIcon fontSize="inherit" />
            </Tooltip>
          )}
        </Stack>
        {readOnly ? (
          <TextField
            fullWidth
            size="small"
            value={data?.[key] || '0'}
            disabled={true}
            sx={{
              backgroundColor: 'action.hover',
            }}
          />
        ) : (
          <TextField
            type="number"
            fullWidth
            size="small"
            value={formData[key]}
            onChange={handleChange(key)}
            onBlur={handleBlur(key)}
            placeholder={placeholder}
            error={!fieldValid}
            helperText={!fieldValid ? errorMsg : ''}
            slotProps={{
              input: { inputProps: { min: min || 0, step } },
            }}
          />
        )}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Edit Info{' '}
        <Typography variant="body2">
          {data?.name} - {data?.weekDay}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 2,
          }}
        >
          {FIELD_CONFIG.map(renderField)}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary" disabled={loading || !isValid}>
          {loading ? <CircularProgress size={20} color="inherit" /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditInfoModal;
